import { type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import {
  ProviderDetail,
  providerDetailQuery,
  providerSections,
} from '@/features/providers';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { useRailSubNav } from '@/app/shell/use-rail-sub-nav';
import { resolveSection } from '@/lib/entity-sections';
import { providerId } from '@/lib/api/ids';

export const Route = createFileRoute('/_auth/providers/$providerId/$section')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      providerDetailQuery(providerId(params.providerId)),
    ),
  component: ProviderDetailPage,
});

/*
 * Injects a typed router <Link> into the domain-free RailSubNav. The rail sub-nav
 * builds the full `/providers/{id}/{section}` href; we pass it straight through
 * to `to` so every param survives — client navigation and deep-linking both
 * work.
 */
function renderLink(props: {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-current'?: 'page';
  'data-section'?: string;
}) {
  const { href, children, ...rest } = props;
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

function ProviderDetailPage() {
  const { providerId: providerIdParam, section } = Route.useParams();
  const id = providerId(providerIdParam);
  const { data: provider } = useSuspenseQuery(providerDetailQuery(id));
  const activeSectionId =
    resolveSection(section, providerSections) ?? providerSections[0]!.id;

  usePageMeta({ title: 'Provider' });
  useRailSubNav({
    sections: providerSections,
    activeSectionId,
    basePath: `/providers/${id}`,
    entityName: provider.name,
    backHref: '/providers',
    backLabel: 'All Providers',
    navLabel: 'Provider sections',
    renderLink,
  });

  return (
    <Page>
      <PageHeader title="Provider" />
      <Section>
        <ProviderDetail providerId={id} section={section} />
      </Section>
    </Page>
  );
}
